import { parseUri } from '@studio/models/net';
import { toProtocol } from '@studio/models/video';
import { composeRtspUrl, isRtsp } from '@studio/models/video/rtsp';
import type { Configuration as RtspConfig } from '@studio/models/video/rtsp';
import { isNotEmpty, isNotNil } from '@tryiris-ai/js-types';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { Brand, Model } from '@studio/models/device';
import type { Collection } from '#~/api/device/registry/index.js';
import { COMMON_FORMAT_CATALOG } from '../../probe/index.js';
import {
  AuthErrorMessage,
  AuthInvalidMessage,
  AuthRequiredMessage,
  ConnectionFailedMessage,
  ConnectionUnreachableMessage,
  ProbeWarnControlUnconfirmedMessage,
  ProbeWarnEmptyAcceptedMessage,
  ProbeWarnUnvalidatedMessage,
  StreamNotFoundMessage
} from './constants.js';
import type { WarnReason } from './auth-probe.js';

/**
 * A selectable stream option presented to the user. `url` is the composed
 * `rtsp://` candidate for that option (undefined while inputs are incomplete,
 * e.g. no IP yet).
 *
 * RTSP Assisted Setup — drives both the Main/Sub picker (template path) and the
 * Brand="Other" format picker (catalog path).
 */
export interface StreamOption {
  /** Stable option id (select value). */
  id: string;
  /** Display label (e.g. "Main", "Sub", or a catalog vendor name). */
  label: string;
  /** Composed `rtsp://` URL for this option, or undefined if not composable. */
  url: T_Maybe<string>;
  /**
   * IP-agnostic `rtsp://<ip>…` URL-template preview shown under the label in
   * the Brand="Other" catalog picker. Unset for the Main/Sub template path.
   */
  preview?: string;
}

/** Credentials shape used to fold user/pass into the composed URL. */
type Creds = T_Maybe<{ pass?: T_Maybe<string>; user?: T_Maybe<string> }>;

/** Parsed IP/host field: the clean host, an optional port, and a validity flag. */
export interface ParsedHostPort {
  /** The host with any trailing `:port` removed. */
  host: string;
  /** The parsed trailing port, when the field carried a valid `:port`. */
  port?: number;
  /** False when the host carries illegal characters or the port is out of range. */
  valid: boolean;
}

/**
 * A dotted-quad IPv4 address: exactly four 1-3 digit octets, each 0..255.
 * Shared by parseHostPort (field validity) and isCompleteHost (auto-attempt
 * gate) so the two never disagree on what a numeric address is.
 * Per rule: investigate-before-adding (ai.knowledge/global) — extracted from
 * isCompleteHost rather than adding a second octet check.
 */
const isIpv4 = (host: string): boolean => {
  const octets = host.split('.');
  return (
    octets.length === 4 && octets.every((octet) => /^\d{1,3}$/.test(octet) && Number(octet) <= 255)
  );
};

/**
 * Parse the user-entered IP/host field into a clean host plus an OPTIONAL
 * trailing port. The RTSP composer treats the WHOLE field as the host, so
 * a raw `10.0.0.5:8554` was folded into a mangled `10.0.0.5-8554` host by
 * `formatUri`. Split a single trailing `:port` out here so the port drives the
 * composed URL and the host stays clean, and reject illegal input so the caller
 * can flag the field instead of composing (and connectivity-checking) a bad URL.
 *
 * A host is valid when it contains only hostname/IPv4 characters
 * (`A-Z a-z 0-9 . -`); a port is valid in 1..65535. A numeric-looking host
 * (digits and dots only) is an IPv4 address and must be a full dotted quad of
 * 0..255 octets, so `999.1` or `192.168.1.256` is flagged instead of composed.
 * IPv6 (bracketed or multi-colon) is out of scope for this field — more than
 * one colon reads as invalid rather than being guessed.
 *
 * @param value - The raw IP/host field value.
 * @returns The clean host, optional port, and whether the field is usable.
 */
export const parseHostPort = (value: string): ParsedHostPort => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { host: '', valid: false };
  }

  // One optional trailing `:port`. Anything else (extra colons, illegal host
  // characters) fails the match or the host test below.
  const match = /^([^:]*)(?::(\d{1,5}))?$/.exec(trimmed);
  if (!match) {
    return { host: trimmed, valid: false };
  }

  const host = match[1] ?? '';
  const port = match[2] !== undefined ? Number(match[2]) : undefined;
  const hostValid = /^[A-Za-z0-9.-]+$/.test(host) && (!/^[\d.]+$/.test(host) || isIpv4(host));
  const portValid = port === undefined || (port >= 1 && port <= 65535);

  // exactOptionalPropertyTypes: omit `port` key entirely when undefined rather
  // than passing `port: undefined` — TS2375 rejects the latter.
  return { host, ...(port !== undefined ? { port } : {}), valid: hostValid && portValid };
};

/** IP field error when the field is blank. */
export const IpAddressRequiredMessage = 'Enter an IP address';

/** IP field error when the field holds a malformed address, host or port. */
export const IpAddressInvalidMessage = 'Enter a valid IP address';

/**
 * Error text for the IP/host field, or undefined when it parses. Shown under the
 * field so a red border is never the only signal of what is wrong.
 *
 * @param value - The raw IP/host field value.
 * @returns The blank or malformed message, or undefined for a usable field.
 */
export const ipAddressError = (value: string): T_Maybe<string> => {
  if (value.trim().length === 0) {
    return IpAddressRequiredMessage;
  }

  return parseHostPort(value).valid ? undefined : IpAddressInvalidMessage;
};

/**
 * Build the Main (+ optional Sub) stream options for a known-model or generic
 * device template. The host comes from the user-entered IP — the template only
 * supplies path/port (the registry `videoHost` is a placeholder). Sub is
 * omitted entirely when the template has no `pathSub` (HIDE Sub when empty).
 *
 * @param video - Resolved registry video template (`useTemplate(id).video`).
 * @param host - User-entered IP address.
 * @param credentials - Optional device credentials.
 * @param portOverride - Port parsed from a `host:port` IP field; when
 *   set it wins over the template's port for both Main and Sub (a camera on a
 *   non-standard port serves both streams there).
 * @returns Ordered options: Main first, then Sub when present.
 */
export const buildTemplateOptions = (
  video: T_Optional<RtspConfig>,
  host: string,
  credentials?: Creds,
  portOverride?: T_Maybe<number>
): StreamOption[] => {
  if (!video) {
    return [];
  }

  const options: StreamOption[] = [
    {
      id: 'main',
      label: 'Main',
      url: composeRtspUrl({ host, path: video.path, port: portOverride ?? video.port }, credentials)
    }
  ];

  // HIDE Sub when the template carries no sub-stream path. The Sub port falls
  // back to the Main port when unset (pbToJsVideoTemplate already applies that
  // fallback into `portSub`); a user-entered `host:port` overrides both.
  if (isNotEmpty(video.pathSub)) {
    options.push({
      id: 'sub',
      label: 'Sub',
      url: composeRtspUrl(
        { host, path: video.pathSub, port: portOverride ?? video.portSub ?? video.port },
        credentials
      )
    });
  }

  return options;
};

/**
 * Build the stream options for the Brand="Other" path from the hardcoded
 * common-format catalog. Each catalog entry's main path becomes one option.
 *
 * @param host - User-entered IP address.
 * @param credentials - Optional device credentials.
 * @param portOverride - Port parsed from a `host:port` IP field; when set
 *   it wins over the catalog entry's default port.
 * @returns One option per catalog entry.
 */
export const buildCatalogOptions = (
  host: string,
  credentials?: Creds,
  portOverride?: T_Maybe<number>
): StreamOption[] =>
  COMMON_FORMAT_CATALOG.map((entry) => ({
    id: entry.id,
    label: entry.label,
    preview: entry.preview,
    url: composeRtspUrl({ host, path: entry.path, port: portOverride ?? entry.port }, credentials)
  }));

/**
 * Matches the catch-all "Other" record by WHOLE WORD, not substring. The device
 * registry marks its generic brand/model with the display word "Other", but a
 * substring test (`.includes('other')`) also fires on real names that merely
 * contain those letters — e.g. "Brother" or "Motherboard" — misdetecting them
 * as the catch-all and hiding the Model selector / swapping the picker. The word
 * boundaries and `i` flag match "Other" / "Other / not listed" while rejecting
 * "Brother".
 */
const CATCH_ALL_NAME = /\bother\b/i;

/**
 * Resolve a brand's GENERIC device-registry model id — the `*_generic.go`
 * record surfaced as the model whose name is the whole word "Other" (mirrors the
 * existing auto-select in manufacturer/details.tsx). Used for the Known-brand +
 * Model="Other" case so `useTemplate(genericModelId).video` yields the brand's
 * generic RTSP template. No API/DR change — a pure client lookup over the
 * already-fetched models collection.
 *
 * @param models - Models collection for the selected brand (`useModels`).
 * @returns The generic model id, or undefined when none is present.
 */
export const findGenericModelId = (models: T_Maybe<Collection<Model>>): T_Maybe<Model['id']> =>
  Object.values(models?.items ?? {}).find((model) => CATCH_ALL_NAME.test(model.name))?.id;

/**
 * Whether a brand is the catch-all "Other" brand (Brand="Other" path).
 *
 * @param brand - The selected brand record.
 */
export const isOtherBrand = (brand: T_Maybe<Brand>): boolean =>
  isNotNil(brand) && CATCH_ALL_NAME.test(brand.name);

/**
 * Check-Connectivity enablement predicate. The button is enabled iff the
 * selected composed URL is a valid, testable RTSP
 * URI (RTSP-family protocol WITH a host) AND differs from the last-checked URL.
 * Credentials NEVER gate — anonymous RTSP is allowed.
 *
 * @param selectedUrl - The composed URL for the currently-selected option.
 * @param lastCheckedUrl - The URL most recently submitted via Check Connectivity.
 * @returns True when the button should be enabled.
 */
export const isCheckConnectivityEnabled = (
  selectedUrl: T_Maybe<string>,
  lastCheckedUrl: T_Maybe<string>
): boolean => {
  if (!isNotEmpty(selectedUrl)) {
    return false;
  }

  // Same URL already checked → nothing new to test.
  if (selectedUrl === lastCheckedUrl) {
    return false;
  }

  const uri = parseUri(selectedUrl);

  // Valid testable RTSP URI requires both an RTSP-family protocol and a host.
  return isNotNil(uri) && isRtsp(toProtocol(uri.protocol)) && isNotEmpty(uri.host);
};

/**
 * Whether a host looks fully typed: a dotted quad needs four octets in 0..255,
 * and a hostname cannot end on a separator. Keeps an automatic attempt from
 * firing on an address the user is still typing.
 *
 * @param host - The host of the stream URL.
 */
export const isCompleteHost = (host: T_Maybe<string>): boolean => {
  if (!host) {
    return false;
  }

  if (/^[\d.]+$/.test(host)) {
    return isIpv4(host);
  }

  return /^[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?$/.test(host) && !host.includes('..');
};

/**
 * Whether a stream URL may be attempted without the user pressing Check
 * Connectivity: a complete RTSP URL, with or without a login. Error telemetry
 * masks URL logins; the wizard attempts a URL with a login only when the
 * password is committed, never on a typing pause.
 *
 * @param url - The effective (credential-bearing when creds are entered) URL.
 */
export const isAutoAttemptUrl = (url: T_Maybe<string>): boolean => {
  if (!isNotEmpty(url)) {
    return false;
  }

  const uri = parseUri(url);

  return isNotNil(uri) && isRtsp(toProtocol(uri.protocol)) && isCompleteHost(uri.host);
};

/** What a failed RTSP connection most likely needs from the user. */
export type ConnectionFailureKind = 'unreachable' | 'stream-not-found' | 'unknown';

// Embedded RTSP URLs are dropped before matching: a path or password could
// contain "404" or "not found".
const RTSP_URL_TOKEN = /\brtsp[a-z]*:\/\/\S+/gi;
const STREAM_NOT_FOUND = /\b404\b|not found/i;
const HOST_UNREACHABLE =
  /could not open resource|could not connect|failed to connect|connection refused|no route to host|network is unreachable|host is (?:down|unreachable)|i\/o timeout/i;

/**
 * Classify the encoder's error for a failed stream (the transient's
 * `video.error`, e.g. the GStreamer failure cause) so the user gets the next
 * step that fits: re-check the address, or pick another format. Anything not
 * clearly one or the other stays `unknown` and keeps the generic guidance.
 *
 * @param error - The video error the encoder reported, if any.
 */
export const classifyConnectionFailure = (error: T_Maybe<string>): ConnectionFailureKind => {
  const text = (error ?? '').replace(RTSP_URL_TOKEN, '');

  if (STREAM_NOT_FOUND.test(text)) {
    return 'stream-not-found';
  }

  return HOST_UNREACHABLE.test(text) ? 'unreachable' : 'unknown';
};

/**
 * The "Connection failed" guidance for a failure kind.
 *
 * @param kind - The classified failure.
 */
export const connectionFailureMessage = (kind: ConnectionFailureKind): string => {
  switch (kind) {
    case 'unreachable':
      return ConnectionUnreachableMessage;
    case 'stream-not-found':
      return StreamNotFoundMessage;
    default:
      return ConnectionFailedMessage;
  }
};

/**
 * Map the video transport's `videoAuthenticationStatus` to a user-facing
 * message (consume-only — mirrors the control screen's Callout). Returns
 * undefined for statuses that should show nothing
 * (VALIDATING / VALIDATED / UNSUPPORTED / UNKNOWN).
 *
 * @param status - The AuthenticationStatus enum value (or undefined).
 * @param credentialsEntered - Whether the attempt carried credentials; a
 *   rejection of an attempt without them means the camera requires a login.
 * @returns The message string, or undefined when no message should render.
 */
export const authStatusMessage = (
  status: T_Maybe<AuthenticationStatus>,
  credentialsEntered = true
): T_Maybe<string> => {
  switch (status) {
    case AuthenticationStatus.AUTHENTICATION_STATUS_INVALID:
      return credentialsEntered ? AuthInvalidMessage : AuthRequiredMessage;
    case AuthenticationStatus.AUTHENTICATION_STATUS_ERROR:
      return AuthErrorMessage;
    default:
      // VALIDATING / VALIDATED / UNSUPPORTED / UNKNOWN (and undefined) → no message.
      return undefined;
  }
};

/**
 * Map an empty-creds-probe warning reason to its user-facing copy. Consume-only,
 * mirrors `authStatusMessage`: keeping the copy selection here (not inline in the
 * component) makes the exact-wording contract a pure, unit-testable mapping.
 *
 * @param reason - The terminal warning reason from the probe machine, or
 *   undefined when the outcome was not a warning (no message).
 * @returns The warning string, or undefined when no warning applies.
 */
export const probeWarningMessage = (reason: T_Maybe<WarnReason>): T_Maybe<string> => {
  switch (reason) {
    case 'empty-accepted':
      return ProbeWarnEmptyAcceptedMessage;
    case 'control-unconfirmed':
      return ProbeWarnControlUnconfirmedMessage;
    case 'unvalidated':
      return ProbeWarnUnvalidatedMessage;
    default:
      return undefined;
  }
};
